/**
 * Chat Configuration
 * Data-driven configuration for the chat system
 */

import { ChatMessage } from '../../game/ui/HUD/ChatTypes';

export interface ChatFilter {
  id: string;
  label: string;
  types: ChatMessage['type'][];
}

export interface ChatShortcut {
  action: string;
  key: string;
  description: string;
}

export const CHAT_CONFIG = {
  /** Maximum number of messages to keep in history */
  maxMessages: 100,
  
  /** Default message to show on load */
  welcomeMessage: {
    id: 'welcome',
    sender: 'System',
    text: 'Welcome to Helix 2000! Press Enter to chat, F8 to pause chat.',
    timestamp: Date.now(),
    type: 'system' as const
  },
  
  /** Available message filters */
  filters: [
    { 
      id: 'all', 
      label: 'All', 
      types: ['global', 'system', 'whisper', 'party', 'guild', 'combat', 'debug'] 
    },
    { 
      id: 'combat', 
      label: 'Combat', 
      types: ['combat'] 
    },
    { 
      id: 'debug', 
      label: 'Debug', 
      types: ['debug', 'system'] 
    }
  ] as ChatFilter[],
  
  /** Keyboard shortcuts */
  shortcuts: {
    /** Focus chat input */
    focus: { key: 'Enter', description: 'Focus chat input' },
    /** Pause chat auto-scroll */
    pause: { key: 'F8', description: 'Pause/resume chat' },
    /** Send message */
    send: { key: 'Enter', description: 'Send message' }
  },
  
  /** Message colors by type */
  messageColors: {
    global: '#ffffff',
    system: '#f1c40f',
    whisper: '#e67e22',
    party: '#3498db',
    guild: '#2ecc71',
    combat: '#e74c3c',
    debug: '#9b59b6'
  } as Record<ChatMessage['type'], string>,
  
  /** Message type labels */
  messageTypeLabels: {
    global: '[GLOBAL]',
    system: '[SYSTEM]',
    whisper: '[WHISPER]',
    party: '[PARTY]',
    guild: '[GUILD]',
    combat: '[COMBAT]',
    debug: '[DEBUG]'
  } as Record<ChatMessage['type'], string>,
  
  /** UI dimensions */
  dimensions: {
    minWidth: '350px',
    minHeight: '200px',
    headerHeight: '44px',
    inputHeight: '48px'
  }
};

/** Get filter by ID */
export const getChatFilter = (filterId: string): ChatFilter => {
  return CHAT_CONFIG.filters.find(f => f.id === filterId) ?? CHAT_CONFIG.filters[0];
};

/** Get color for message type */
export const getMessageColor = (type: ChatMessage['type']): string => {
  return CHAT_CONFIG.messageColors[type] ?? CHAT_CONFIG.messageColors.global;
};

/** Get label for message type */
export const getMessageTypeLabel = (type: ChatMessage['type']): string => {
  return CHAT_CONFIG.messageTypeLabels[type] ?? CHAT_CONFIG.messageTypeLabels.global;
};
